import { makeStyles } from '@material-ui/styles'
import useReactRouter from 'use-react-router'

import { useAppSelector } from 'app/store'
import PollingData from 'core/components/PollingData'
import { ViewMode } from 'core/components/ViewModeToggle'
import Progress from 'core/components/progress/Progress'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import { IAppsDetailsPageTabs } from '../../../model'
import { listApplications, listTemplates } from '../../../repositories/actions'
import { myAppSelector, templateSelector } from '../../../repositories/selector'
import AppHeaderPrimaryActions from '../../AppHeaderPrimaryActions'
import CommonActionPopover from '../../CommonActionPopover'
import VmsDetailsModal from '../../VmsDetailsModal'
import { overviewActions } from '../../helper'
import AppCardView from '../AppCardView'
import AppListView from '../AppListView'
import CreateNewAppModal from '../CreateNewAppModal'
import DeleteApp from '../DeleteApp'
import { gridContainer } from '../../commonStyles'
import { disabledCreateAppAdminMessage, disabledCreateAppSSUUserMessage } from '../../constant'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    paddingBottom: theme.spacing(1),
  },
  gridContainer: gridContainer(theme),
  gridItem: {
    width: '100%',
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  appInfoFooterTitle: {
    color: theme.components.typography.passive,
    textTransform: 'uppercase',
  },
  divider: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  sharedContainer: {
    marginTop: theme.spacing(3),
  },
  progressOverlay: {
    maxHeight: '50%',
    boxSizing: 'border-box',
  },
  emptyContent: {
    paddingTop: '10%',
  },
}))

const MyApps = () => {
  const [view, setView] = useState<ViewMode>(ViewMode.CARD)
  const classes = useStyles()
  const { history } = useReactRouter()
  const oneSecond = 1000
  const { loading, reload } = useListAction(listApplications)
  const { loading: loadingTemplates, reload: reloadTemplates } = useListAction(listTemplates)
  const templateDetails = useAppSelector(templateSelector)
  const appDetails = useAppSelector(myAppSelector)
  const [filteredData, setFilteredData] = useState(appDetails)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showVmsModal, setShowVmsModal] = useState(false)
  const [rowData, setRowData] = useState(null)
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedApp, setSelectedApp] = useState(null)
  // Commented out for now, as the shared with me section is not currently implemented
  // const [sharedApps, nonSharedApps] = splitByIsShared(filteredData)
  // const sharedAppDetails = sharedApps
  // const nonSharedAppDetails = nonSharedApps

  const handlePopoverClick = useCallback((event, item) => {
    setAnchorEl(event.currentTarget)
    setSelectedApp(item) // set the selected card's data
    setOpen(true)
  }, [])

  const handleSearch = useCallback(
    (filteredResults) => {
      setFilteredData(filteredResults?.appDetails)
    },
    [appDetails],
  )

  const handleReload = useCallback(async (refetch = false, updateLoadingState = true) => {
    reload(refetch, updateLoadingState)
    reloadTemplates(refetch, updateLoadingState)
  }, [])

  useEffect(() => {
    setFilteredData(appDetails)
  }, [appDetails])

  return (
    <>
      <PollingData
        hidden
        loading={loading || loadingTemplates}
        onReload={handleReload}
        refreshDuration={oneSecond * 30}
      />
      <CreateNewAppModal
        addRoute={routeManager.routes.openstack.createApps}
        tabName={IAppsDetailsPageTabs.Myapps}
      />
      {showDeleteDialog && selectedApp && (
        <DeleteApp
          rows={[selectedApp]}
          onClose={() => {
            setShowDeleteDialog(false)
            setSelectedApp(null)
          }}
        />
      )}
      {showVmsModal && (
        <VmsDetailsModal
          data={rowData}
          showVmsModal={showVmsModal}
          setShowVmsModal={setShowVmsModal}
        />
      )}
      {/* <VmSlider value={vmCount} onChange={setVmCount} /> */}
      <AppHeaderPrimaryActions
        addUrl={routeManager.routes.openstack.createApps.path({
          tab: IAppsDetailsPageTabs.Myapps,
        })}
        addText="Create New App"
        disableAddButton={!templateDetails?.length}
        disabledAddButtonInfo={
          isAdmin() ? disabledCreateAppAdminMessage : disabledCreateAppSSUUserMessage
        }
        view={view}
        setView={setView}
        handleSearch={handleSearch}
        data={{ appDetails }}
        searchKeys={['headerName', 'templateName', 'ownerName']}
        onReload={handleReload}
      >
        <Progress
          loading={loading}
          message={'Loading My Apps...'}
          overlay
          className={classes.progressOverlay}
        >
          {filteredData && filteredData?.length > 0 ? (
            view === ViewMode.LIST ? (
              <>
                {filteredData?.map((item) => (
                  <div className={classes.grid} key={item.uid}>
                    <AppListView
                      iconName={item?.iconName}
                      headerName={item?.headerName}
                      status={item?.status}
                      vms={item?.vms}
                      version={item?.version}
                      templateName={item?.templateName}
                      ownerName={item?.ownerName}
                      overviewActions={overviewActions(item, setShowVmsModal, setRowData)}
                      handleClick={(e) => handlePopoverClick(e, item)}
                      isIconView={true}
                      data={item}
                    />
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className={classes.gridContainer}>
                  {filteredData?.map((item) => (
                    <div key={item?.uid} className={classes.gridItem}>
                      <AppCardView
                        iconName={item?.iconName}
                        headerName={item?.headerName}
                        status={item?.status}
                        vms={item?.vms}
                        version={item?.version}
                        templateName={item?.templateName}
                        ownerName={item?.ownerName}
                        overviewActions={overviewActions(item, setShowVmsModal, setRowData)}
                        handleClick={(e) => handlePopoverClick(e, item)}
                        isIconView={true}
                        data={item}
                      />
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            <div className={classes.emptyContent}>
              <EmptyContent
                title={'No Apps Found'}
                subTitle={'Create one in order to see details'}
              />
            </div>
          )}
        </Progress>
      </AppHeaderPrimaryActions>
      <CommonActionPopover
        open={open}
        setOpen={setOpen}
        anchorEl={anchorEl}
        actions={[
          // {
          //   label: 'Edit',
          //   icon: 'edit',
          //   onClick: () => {
          //     history.push(
          //       routeManager.routes.openstack.createApps.path({
          //         tab: IAppsDetailsPageTabs.Myapps,
          //       }),
          //     )
          //   },
          //   disabled: false,
          //   tooltipMessage: 'NS and SOA type records cannot be edited.',
          // },
          {
            label: 'Delete',
            icon: 'trash-can',
            onClick: () => {
              setShowDeleteDialog(true)
              setOpen(false)
            },
            disabled: selectedApp?.status === 'deleting' ? true : false,
          },
        ]}
      />
    </>
  )
}

export default MyApps
